// Staff chat MVP (SR-14): the TanStack Query hooks over staff-chat-api.ts. Every hook takes `enabled` so nothing is
// read with the `?x=staff-chat` switch off. Keys are per organisation and user (staff-chat-keys.ts).
//
// Live: the Notifications hub (use-staff-chat-hub.ts) folds events into these caches; the unread total and the thread
// list are also read again on window focus (TanStack default) so a missed event heals itself.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/auth/auth-store';
import {
  createThread,
  fetchDirectory,
  fetchMessages,
  fetchThread,
  fetchThreads,
  fetchUnread,
  markThreadRead,
  sendMessage,
} from './staff-chat-api';
import { staffChatKeys } from './staff-chat-keys';
import {
  foldMessageIntoThreads,
  keepUnsentMessages,
  markMessageFailed,
  upsertMessage,
  type MessagesData,
  type ThreadsData,
} from '@/staff-chat/realtime/staff-chat-cache';
import { whenHubNotReconnecting } from '@/staff-chat/lib/hub-status-store';
import { staffChatRetry } from '@/staff-chat/lib/staff-chat-errors';
import type { CreateStaffChatThread, LocalStaffChatMessage, StaffChatThread } from '@/staff-chat/types/staff-chat';

/** `${organizationId}|${userId}` of the signed-in user, and the user id alone. */
export function useStaffChatIdentity(): { who: string; me: string | undefined } {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const me = useAuthStore((s) => s.user?.userId);
  return { who: `${orgId ?? ''}|${me ?? ''}`, me };
}

export function useStaffDirectory(enabled = true) {
  const { who, me } = useStaffChatIdentity();
  return useQuery({
    queryKey: staffChatKeys(who).directory(),
    enabled: enabled && !!me,
    retry: staffChatRetry,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => fetchDirectory(signal),
  });
}

export function useStaffThreads(enabled = true) {
  const { who, me } = useStaffChatIdentity();
  return useQuery<ThreadsData>({
    queryKey: staffChatKeys(who).threads(),
    enabled: enabled && !!me,
    retry: staffChatRetry,
    queryFn: ({ signal }) => fetchThreads(null, signal),
  });
}

export function useStaffThread(threadId: string | undefined, enabled = true) {
  const { who, me } = useStaffChatIdentity();
  const qc = useQueryClient();
  return useQuery<StaffChatThread>({
    queryKey: staffChatKeys(who).thread(threadId ?? ''),
    enabled: enabled && !!me && !!threadId,
    retry: staffChatRetry,
    queryFn: ({ signal }) => fetchThread(threadId!, signal),
    // The list already holds this thread: show it at once, then read the full one.
    placeholderData: () => qc.getQueryData<ThreadsData>(staffChatKeys(who).threads())?.items.find((t) => t.id === threadId),
  });
}

export function useThreadMessages(threadId: string | undefined, enabled = true) {
  const { who, me } = useStaffChatIdentity();
  return useInfiniteQuery({
    queryKey: staffChatKeys(who).messages(threadId ?? ''),
    enabled: enabled && !!me && !!threadId,
    retry: staffChatRetry,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => fetchMessages(threadId!, pageParam, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // A re-read keeps the messages still sending or failed (with their Retry): they are not on the server yet.
    structuralSharing: keepUnsentMessages,
  });
}

export function useStaffChatUnread(enabled = true) {
  const { who, me } = useStaffChatIdentity();
  return useQuery<number>({
    queryKey: staffChatKeys(who).unread(),
    enabled: enabled && !!me,
    retry: staffChatRetry,
    queryFn: ({ signal }) => fetchUnread(signal),
  });
}

/** Find or create the thread with one colleague (and a patient, for a patient thread). */
export function useCreateStaffThread() {
  const { who } = useStaffChatIdentity();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStaffChatThread) => createThread(body),
    onSuccess: (thread) => {
      qc.setQueryData(staffChatKeys(who).thread(thread.id), thread);
      void qc.invalidateQueries({ queryKey: staffChatKeys(who).threads() });
    },
  });
}

export interface SendInput {
  body: string;
  /** Kept on a retry, so the server answers the first message instead of creating a second. */
  clientMessageId: string;
}

/**
 * Optimistic send: the message shows at once as "sending", is replaced by the server's copy on success (matched on the
 * clientMessageId), and stays as "failed" with a retry on error. While the hub is reconnecting it waits, then goes.
 */
export function useSendStaffMessage(threadId: string) {
  const { who, me } = useStaffChatIdentity();
  const qc = useQueryClient();
  const keys = staffChatKeys(who);
  return useMutation({
    // While the live hub is reconnecting the message waits ("Sending…") and goes when it is back (the dock's strip says so).
    mutationFn: async ({ body, clientMessageId }: SendInput) => {
      await whenHubNotReconnecting();
      return sendMessage(threadId, body, clientMessageId);
    },
    onMutate: async ({ body, clientMessageId }) => {
      await qc.cancelQueries({ queryKey: keys.messages(threadId) });
      const pending: LocalStaffChatMessage = {
        id: `pending:${clientMessageId}`,
        threadId,
        senderUserId: me ?? '',
        body,
        createdAt: new Date().toISOString(),
        clientMessageId,
        isDeleted: false,
        sendState: 'sending',
      };
      qc.setQueryData<MessagesData>(keys.messages(threadId), (d) =>
        upsertMessage(d ?? { pages: [], pageParams: [] }, pending),
      );
    },
    onSuccess: (message) => {
      qc.setQueryData<MessagesData>(keys.messages(threadId), (d) => upsertMessage(d, message));
      qc.setQueryData<ThreadsData>(keys.threads(), (d) => foldMessageIntoThreads(d, message, me, threadId).data);
    },
    onError: (_err, { clientMessageId }) => {
      qc.setQueryData<MessagesData>(keys.messages(threadId), (d) => markMessageFailed(d, clientMessageId));
    },
  });
}

/** Marks every message in the thread read for the caller; the thread's count goes to 0 at once. */
export function useMarkStaffThreadRead() {
  const { who } = useStaffChatIdentity();
  const qc = useQueryClient();
  const keys = staffChatKeys(who);
  /** The thread's count to 0 in the list and in the thread; returns what was cleared. */
  const clear = (threadId: string): number => {
    let cleared = 0;
    qc.setQueryData<ThreadsData>(keys.threads(), (d) => {
      if (!d) return d;
      return {
        ...d,
        items: d.items.map((t) => {
          if (t.id !== threadId) return t;
          cleared = t.unreadCount;
          return t.unreadCount === 0 ? t : { ...t, unreadCount: 0 };
        }),
      };
    });
    qc.setQueryData<StaffChatThread>(keys.thread(threadId), (t) => (t && t.unreadCount !== 0 ? { ...t, unreadCount: 0 } : t));
    return cleared;
  };
  return useMutation({
    mutationFn: (threadId: string) => markThreadRead(threadId),
    onMutate: (threadId) => {
      // No cancel of the list read here: a cancelled FIRST list load stays pending and never restarts.
      const cleared = clear(threadId);
      if (cleared > 0) qc.setQueryData<number>(keys.unread(), (n) => (typeof n === 'number' ? Math.max(0, n - cleared) : n));
    },
    onSuccess: (_void, threadId) => {
      // A list that arrived between the mutate and the server's answer may still carry the old count: clear it again.
      clear(threadId);
    },
    onSettled: (_void, error, threadId) => {
      // The server's total is the truth (the hub's StaffChatUnread also sets it).
      void qc.invalidateQueries({ queryKey: keys.unread() });
      // Read the list again after the read is stored. This replaces any stale list read still in flight.
      // When it lands, the opened thread stays at 0 even if the server count lags.
      void qc.invalidateQueries({ queryKey: keys.threads() }).then(() => {
        if (!error) clear(threadId);
      });
    },
  });
}
